import React from "react";
import tw from "twin.macro";
import type { CSSProp } from "styled-components";
import { m, useMotionValue, useMotionValueEvent, useReducedMotion, useScroll, useSpring } from "framer-motion";

/**
 * Scoped, opt-in image parallax.
 *
 * A page enables the effect by wrapping its content in `ImageParallaxProvider`
 * (driven by the Sanity `enableImageParallax` route toggle). The shared `Image`
 * component reads this context and, for eligible images, delegates rendering to
 * `ParallaxImg`. The framer-motion scroll hooks therefore only ever run for
 * images on a parallax-enabled page — never site-wide.
 *
 * Videos never reach `Image` (they render through the video branch of
 * `craft/asset`), so they are excluded for free. SVGs, GIFs and `priority`
 * (LCP / hero) images are excluded by `Image` before it reaches this module.
 */

type ImageParallaxContextValue = {
  enabled: boolean;
  /** Upscale factor; travel is the headroom it creates, (scale - 1) / 2 of the frame each way. */
  scale?: number;
  /**
   * `spring` (default) eases the image toward its target; `direct` sets it from the scroll
   * position every frame. Lenis already smooths the page scroll, so `direct` keeps the image
   * locked to the page instead of trailing it.
   */
  tracking?: "spring" | "direct";
  /**
   * `headroom` (3.0): a cover image is drawn in a box taller than its frame by the travel at each
   * end and pans through it, instead of being scaled up: it shows at the scale the design crops it,
   * only zooming as far as the source lacks the height. Other fits keep the scale.
   */
  mode?: "scale" | "headroom";
};

const ImageParallaxContext = React.createContext<ImageParallaxContextValue>({ enabled: false });

export function ImageParallaxProvider({
  enabled,
  scale,
  tracking,
  mode,
  children,
}: {
  enabled: boolean;
  scale?: number;
  tracking?: "spring" | "direct";
  mode?: "scale" | "headroom";
  children: React.ReactNode;
}) {
  const value = React.useMemo(() => ({ enabled, scale, tracking, mode }), [enabled, scale, tracking, mode]);
  return <ImageParallaxContext.Provider value={value}>{children}</ImageParallaxContext.Provider>;
}

export function useImageParallax() {
  return React.useContext(ImageParallaxContext);
}

// The classic "oversized image panning inside a clipped frame" parallax: the image is upscaled by SCALE to
// create headroom of `frameHeight * (SCALE - 1) / 2` beyond each edge of the
// clipped figure, then translated across exactly that headroom while the frame
// crosses the viewport. Travel is therefore PROPORTIONAL to the frame height
// (±7.5% of it), which is what makes the motion read as one consistent depth
// plane across differently sized blocks — and by construction the pan can
// never reveal a gap at either edge.
const SCALE = 1.15;

// Only parallax content-sized images; skip logos, icons and small UI graphics.
// Evaluated against the frame's rendered height (re-checked on load/resize).
const MIN_FRAME_HEIGHT = 280;

// Scrub smoothing — THE tuning knob for how tightly the image tracks the
// scroll. Higher stiffness = starts moving sooner and settles faster after the
// wheel stops; this is a tight ~0.15s trail. Drop the spring entirely
// (`const y = yTarget`) for exact 1:1 tracking with no trail. Keep damping ≥
// 2*sqrt(stiffness) (critically damped) so it never overshoots and reveals an
// edge of the clipped image.
export const PARALLAX_SPRING = { stiffness: 700, damping: 55, mass: 1 } as const;

const clamp01 = (value: number) => Math.min(1, Math.max(0, value));

// framer-motion redefines the drag/animation event handlers on motion
// components, so drop the conflicting DOM handlers from the base img props
// (we never pass them). Everything else spreads cleanly onto `m.img`.
type MotionConflictingHandlers =
  | "onDrag"
  | "onDragStart"
  | "onDragEnd"
  | "onDragEnter"
  | "onDragExit"
  | "onDragLeave"
  | "onDragOver"
  | "onAnimationStart"
  | "onAnimationEnd"
  | "onAnimationIteration";

type ParallaxImgProps = Omit<
  React.ComponentPropsWithoutRef<"img">,
  "ref" | "style" | "css" | MotionConflictingHandlers
> & {
  /** The figure that wraps (and clips) this image; drives scroll progress. */
  containerRef: React.RefObject<HTMLElement | null>;
  imgRef?: React.Ref<HTMLImageElement>;
  style?: React.CSSProperties;
  /** twin/styled-components styles applied to the image element. */
  imgCss?: CSSProp;
  /** The image fills its frame (`object-fit: cover`): eligible for the headroom mode. */
  cover?: boolean;
};

/**
 * Renders an `<img>` that pans vertically inside its clipped figure, synced to
 * page scroll across the frame's ENTIRE on-screen life: progress 0 when the
 * frame's top edge enters at the viewport bottom, 1 when its bottom edge exits
 * at the viewport top (GSAP's `start: "top bottom", end: "bottom top"`). The
 * image never freezes while visible.
 *
 * Position is re-derived from `getBoundingClientRect()` on every scroll change
 * rather than from cached document offsets — so ancestor transforms (section
 * reveal animations) and late layout shifts (lazy content above) can never
 * leave the effect running against stale coordinates. Honors
 * `prefers-reduced-motion`.
 */
export function ParallaxImg(props: ParallaxImgProps) {
  const { containerRef, imgRef, style, imgCss, cover = false, alt = "", ...imgProps } = props;

  const prefersReducedMotion = useReducedMotion();
  const { scrollY } = useScroll();
  const { scale: contextScale, tracking = "spring", mode = "scale" } = React.useContext(ImageParallaxContext);
  const scale = contextScale ?? SCALE;
  const headroom = mode === "headroom" && cover;
  const travelFraction = (scale - 1) / 2;
  // Excluded = too small, or inside a `data-noparallax` element (`data-noparallax="below-lg"`
  // opts out only below 1024px). Re-evaluated on
  // every measure so lazy images (0px until loaded) qualify once laid out.
  const [excluded, setExcluded] = React.useState(false);
  // Active = near the viewport (IntersectionObserver). Off-screen images do NO
  // per-scroll work: no layout reads, no spring ticks, no `will-change` GPU
  // layer. On an image-heavy page this reduces the per-frame cost from every
  // image on the page to only the handful currently visible.
  const [active, setActive] = React.useState(false);
  const activeRef = React.useRef(false);
  const excludedRef = React.useRef(false);
  const initializedRef = React.useRef(false);

  const yTarget = useMotionValue(0);
  const y = useSpring(yTarget, PARALLAX_SPRING);

  // -travel while entering (image sits higher) → 0 at viewport center →
  // +travel while exiting: the image pans the same direction the page moves
  // but slower, so it reads as a distant depth plane. Scrolling down moves the
  // image down within its frame; scrolling up moves it up. Viewport-relative
  // rect math; no cached offsets.
  const update = React.useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight || 1;
    const progress = clamp01((vh - rect.top) / (vh + rect.height));
    const travel = rect.height * travelFraction;
    yTarget.set(travel * (2 * progress - 1));
    if (!initializedRef.current) {
      initializedRef.current = true;
      // First paint (or a load mid-page): snap into position, don't animate.
      y.jump(yTarget.get());
    }
  }, [containerRef, yTarget, y, travelFraction]);

  const measure = React.useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const optOut = el.closest("[data-noparallax]");
    const isExcluded =
      rect.height < MIN_FRAME_HEIGHT ||
      (optOut !== null && (optOut.getAttribute("data-noparallax") !== "below-lg" || window.innerWidth < 1024));
    excludedRef.current = isExcluded;
    setExcluded(isExcluded);
    update();
  }, [containerRef, update]);

  // Hot path — runs on every scroll frame for every parallax image. Refs (not
  // state) keep the early exit allocation- and render-free; the layout read in
  // `update()` only happens for on-screen, eligible images.
  useMotionValueEvent(scrollY, "change", () => {
    if (!activeRef.current || excludedRef.current) return;
    update();
  });

  // Measure before first paint so ineligible images never flash the transform.
  React.useLayoutEffect(() => {
    measure();
  }, [measure]);

  // Activate/deactivate with a one-viewport margin so the image is already in
  // position (and its spring settled) before it becomes visible, and so the
  // browser can drop its compositor layer once it is comfortably off-screen.
  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        if (!entry) return;
        activeRef.current = entry.isIntersecting;
        setActive(entry.isIntersecting);
        if (entry.isIntersecting) update();
      },
      { rootMargin: "100% 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [containerRef, update]);

  // Re-measure on layout changes (image load, responsive reflow, resize).
  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [measure, containerRef]);

  if (prefersReducedMotion || excluded) {
    return <img ref={imgRef} alt={alt} css={imgCss} style={style} {...imgProps} />;
  }

  // Headroom: the box grows by the travel at both ends (percentages of the frame height), so
  // panning it never shows an edge and the image keeps its cover scale for the taller box.
  const headroomStyle = headroom
    ? { top: `${-travelFraction * 100}%`, height: `${(1 + 2 * travelFraction) * 100}%` }
    : undefined;

  return (
    <m.img
      ref={imgRef}
      alt={alt}
      css={[imgCss, active && tw`will-change-transform`]}
      style={{ ...style, ...headroomStyle, y: tracking === "direct" ? yTarget : y, scale: headroom ? 1 : scale }}
      {...imgProps}
    />
  );
}
